import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import TechLogo from './TechLogo';

const toolIcons = [
  { name: 'Make', icon: 'https://images.ctfassets.net/un655fb9wln6/11sTQC2LhhAtJyb7ptQvEl/6c2dbec03b3a2830a24e09e913ad46cc/Make-app-icon.png', color: '#000000' },
  { name: 'Zapier', icon: 'https://cdn.worldvectorlogo.com/logos/zapier-1.svg', color: '#FF4A00' },
  { name: 'AWS', icon: 'https://cdn.worldvectorlogo.com/logos/aws-2.svg', color: '#FF9900' },
  { name: 'Tableau', icon: 'https://cdn.worldvectorlogo.com/logos/tableau-software.svg', color: '#E97627' },
  { name: 'Binance', icon: 'https://cdn.worldvectorlogo.com/logos/binance-logo.svg', color: '#F3BA2F' },
  { name: 'Slack', icon: 'https://cdn.worldvectorlogo.com/logos/slack-new-logo.svg', color: '#4A154B' },
  { name: 'Stripe', icon: 'https://cdn.worldvectorlogo.com/logos/stripe-4.svg', color: '#008CDD' },
  { name: 'Notion', icon: 'https://upload.wikimedia.org/wikipedia/commons/e/e9/Notion-logo.svg', color: '#000000' },
  { name: 'GitHub', icon: 'https://cdn.worldvectorlogo.com/logos/github-icon-1.svg', color: '#181717' },
  { name: 'Docker', icon: 'https://cdn.worldvectorlogo.com/logos/docker.svg', color: '#2496ED' },
  { name: 'MongoDB', icon: 'https://cdn.worldvectorlogo.com/logos/mongodb-icon-1.svg', color: '#47A248' },
  { name: 'Redis', icon: 'https://cdn.worldvectorlogo.com/logos/redis.svg', color: '#DC382D' },
  { name: 'PostgreSQL', icon: 'https://cdn.worldvectorlogo.com/logos/postgresql.svg', color: '#336791' },
  { name: 'React', icon: 'https://cdn.worldvectorlogo.com/logos/react-2.svg', color: '#61DAFB' },
  { name: 'Vue', icon: 'https://cdn.worldvectorlogo.com/logos/vue-9.svg', color: '#4FC08D' },
  { name: 'Angular', icon: 'https://cdn.worldvectorlogo.com/logos/angular-icon-1.svg', color: '#DD0031' },
];

const Hero = () => (
  <section className="relative overflow-hidden bg-[#f6f3ed]">
    <div
      className="absolute inset-0 opacity-60"
      aria-hidden="true"
      style={{
        backgroundImage:
          'linear-gradient(rgba(17,24,39,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(17,24,39,0.045) 1px, transparent 1px)',
        backgroundSize: '32px 32px',
      }}
    />
    <div className="absolute -right-32 top-0 h-80 w-80 rounded-full bg-neon-orange/15 blur-3xl" aria-hidden="true" />

    <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:px-8 lg:py-28">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55 }}
        className="max-w-3xl"
      >
        <div className="mb-6 inline-flex items-center gap-3 rounded-full border border-gray-900/10 bg-white/80 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-gray-700 shadow-sm backdrop-blur">
          <span className="h-2 w-2 rounded-full bg-neon-orange" aria-hidden="true" />
          Systèmes digitaux pour PME francophones
        </div>
        <h1 className="text-4xl font-bold leading-[1.02] tracking-[-0.04em] text-gray-950 sm:text-5xl lg:text-7xl">
          Transformez votre présence digitale en{' '}
          <span className="text-neon-orange">système commercial.</span>
        </h1>
        <p className="mt-7 max-w-2xl text-lg leading-8 text-gray-600 sm:text-xl">
          Nous clarifions votre priorité, connectons les bons outils et construisons un parcours
          simple pour être trouvé, convertir les demandes et suivre chaque opportunité.
        </p>
        <div className="mt-9">
          <Link
            to="/reserver"
            className="inline-flex min-h-14 w-full items-center justify-center rounded-full bg-gray-950 px-7 py-4 text-base font-bold text-white shadow-xl shadow-gray-950/15 transition hover:-translate-y-0.5 hover:bg-neon-orange focus:outline-none focus:ring-4 focus:ring-neon-orange/25 sm:w-auto"
          >
            Demander mon diagnostic
            <span className="ml-3" aria-hidden="true">↗</span>
          </Link>
          <p className="mt-4 text-sm text-gray-500">
            Un échange de cadrage pour identifier le prochain chantier utile, sans vous imposer une pile d’outils.
          </p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="relative flex min-h-[360px] items-center justify-center sm:min-h-[520px] lg:min-h-[600px]"
        aria-label="Couteau Suisse Digital entouré de ses outils technologiques"
      >
        <motion.div
          className="relative aspect-square w-full max-w-[600px]"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1, ease: 'easeOut' }}
        >
          <motion.div
            className="absolute inset-[8%] rounded-full bg-gradient-to-br from-neon-orange/25 to-transparent blur-3xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            aria-hidden="true"
          />

          <motion.div
            className="absolute inset-0 z-10 flex items-center justify-center"
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img
              src="/swiss-army-knife.svg"
              alt="Couteau Suisse Digital"
              className="h-[58%] w-[58%] object-contain drop-shadow-2xl"
            />
          </motion.div>

          {[0, 1].map((orbitIndex) => (
            <motion.div
              key={`orbit-${orbitIndex}`}
              className="absolute inset-0"
              animate={{ rotate: 360 }}
              transition={{ duration: 20 + orbitIndex * 5, repeat: Infinity, ease: 'linear' }}
              aria-hidden="true"
            >
              {toolIcons.slice(orbitIndex * 8, (orbitIndex + 1) * 8).map((tool, index) => {
                const angle = (index / 8) * Math.PI * 2;
                const radius = 33 + orbitIndex * 13;

                return (
                  <motion.div
                    key={tool.name}
                    className="absolute"
                    style={{
                      left: `calc(50% + ${Math.cos(angle) * radius}%)`,
                      top: `calc(50% + ${Math.sin(angle) * radius}%)`,
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <motion.div
                      className="h-8 w-8 rounded-lg bg-white p-1.5 shadow-lg transition-shadow duration-300 hover:shadow-xl sm:h-10 sm:w-10 sm:rounded-xl sm:p-2 lg:h-12 lg:w-12"
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      transition={{ duration: 0.3 }}
                    >
                      <TechLogo
                        name={tool.name}
                        src={tool.icon}
                        color={tool.color}
                        className="h-full w-full text-xl"
                        imgClassName="h-full w-full object-contain"
                      />
                    </motion.div>
                  </motion.div>
                );
              })}
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </div>
  </section>
);

export default Hero;
